/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  OnDestroy,
} from '@angular/core';
import { Router } from '@angular/router';
import { HomePageHttpService } from '../_services/home-page-http.service';
import { UtilService } from '@services/util.service';
import { SubscriptionPendingService } from 'src/app/shared/dialogs/subscription-pending/subscription-pending.service';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { isCloudFlagSelector, userInfoRoles } from 'src/app/store/app.selectors';
import { FacNavbarService } from 'src/app/layout/navbar/navbar.service';
import { Observable, Subject } from 'rxjs';
import { SidebarService } from 'src/app/layout/sidebar/services/sidebar.service';
import { takeUntil } from 'rxjs/operators';
import { DocsService, DOCS_PATHS, CloudUserRoles } from '@foglight/angular-common';

@Component({
  selector: 'fc-welcome-page',
  templateUrl: './welcome-page.component.html',
  styleUrls: ['./welcome-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WelcomePageComponent implements OnInit, OnDestroy {
  @ViewChild('agentBtn') agentButton: ElementRef;
  home = { icon: 'fac-home' };
  theme = 'light-theme';
  isExternalfglamRequired = false;
  subscriptionTypes: string[] = [];
  iconStyle = { 'margin-top': '-1px' };
  isCloudUser = false;
  showWelcomePage = false;
  isDbcartridgeInstalled = false;
  isEvolveCartridgeInstalled = false;
  evolveCartridgesList = [
    'system:virtualvmw',
    'system:HyperV',
    'system:cloud_manager',
    'system:docker',
  ];
  isCloudEnv$: Observable<boolean>;
  isCloud = false;
  navBarItems: Array<any> = [];
  items = [
    {
      label: 'Foglight Agent Manager for Windows',
      icon: 'fac-windows',
      disabled: false,
      tooltipText: '',
    },
    {
      label: 'Foglight Agent Manager for Linux',
      icon: 'fac-linux',
      disabled: false,
      tooltipText: '',
    },
  ];
  selectedItem;
  arrowState = true;
  clientVersion: any;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private homePageHttpService: HomePageHttpService,
    private cd: ChangeDetectorRef,
    private store: Store,
    private utilService: UtilService,
    private subscriptionPendingService: SubscriptionPendingService,
    private titleService: Title,
    private navbarService: FacNavbarService,
    public sidebarService: SidebarService,
    private docsService: DocsService
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem('theme')) {
      this.theme = localStorage.getItem('theme');
    }

    this.verifyIsCloudEnv();
    this.verifyIsCloudUser();
    this.getFglamClientInstance();
    this.utilService.clearBreadCrumbHistory();
    this.addListenerForThemeChange();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addListenerForThemeChange(): void {
    window.addEventListener('themeChanged', (event: CustomEvent) => {
      if (event.detail == 'dark-theme') {
        this.theme = 'dark-theme';
      } else {
        this.theme = 'light-theme';
      }

      this.cd.detectChanges();
    });
  }

  verifyIsCloudEnv(): void {
    this.isCloudEnv$ = this.store.select(isCloudFlagSelector);
    this.isCloudEnv$.pipe(takeUntil(this.destroy$)).subscribe((isCloudEnv) => {
      this.isCloud = isCloudEnv;
      if (!isCloudEnv) {
        this.getInstalledCartridgeInstances();
      }
    });
  }

  getInstalledCartridgeInstances(): void {
    this.sidebarService.navItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.navBarItems = items;
        if (this.navBarItems.length) {
          this.isDbcartridgeInstalled = this.navBarItems.some((item) => {
            return item.id == 'system:dbwc';
          });
          this.isEvolveCartridgeInstalled = this.navBarItems.some((item) => {
            return this.evolveCartridgesList.includes(item.id);
          });

          this.cd.detectChanges();
        }
      });
  }

  verifyIsCloudUser(): void {
    this.store.select(userInfoRoles).pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      if (
        roles?.includes(CloudUserRoles.FoglightCloudUser) &&
        !roles?.includes(CloudUserRoles.FoglightCloudOwner) &&
        !roles?.includes(CloudUserRoles.FoglightCloudOperator) &&
        !roles?.includes(CloudUserRoles.FoglightCloudAdministrator)
      ) {
        this.isCloudUser = true;
        this.items.forEach((item) => {
          item.disabled = true;
          item.tooltipText =
            'To download an Agent Manager, contact your Foglight Cloud Administrator to get additional roles assigned to your account';
        });
      }
    });
  }

  setCurrentTab(tabName: string, title: string): void {
    this.titleService.setTitle(`${title} - Foglight`);

    localStorage.setItem('currentTab', tabName);
    this.router.navigate(['/notification-management/' + tabName]);
  }

  /**
   * Checking FglamClientInstance to show download link
   */
  getFglamClientInstance(): void {
    if (this.isCloud) {
      this.checkFglamClientInstanceForCloud();
    } else {
      this.checkFglamClientInstanceForOnPrem();
    }
  }

  checkFglamClientInstanceForCloud(): void {
    this.homePageHttpService
      .getFglamClientInstance()
      .subscribe((fglamInstance) => {
        this.showWelcomePage = true;

        if (fglamInstance && fglamInstance.length < 2) {
          this.isExternalfglamRequired = true;
        }

        this.cd.markForCheck();
      });
  }

  checkFglamClientInstanceForOnPrem(): void {
    this.homePageHttpService
      .checkIsExternalFglamClientInstanceExists()
      .then((isExternalFglamClientInstanceExists) => {
        this.showWelcomePage = true;

        if (!isExternalFglamClientInstanceExists) {
          this.isExternalfglamRequired = true;
        }

        this.cd.markForCheck();
      });
  }

  goToAgentMangers(): void {
    this.router.navigateByUrl(
      'wcf?name=general-view-aui-wrapper&viewId=system:administration_agents_agenthost.agentHosts'
    );
  }

  goToVideo(): void {
    const docsUrl = this.docsService.getDocsUrl(DOCS_PATHS.CLOUD_INSTALL_FGLAM_VIDEO);
    window.open(docsUrl, '_blank');
  }

  redirectTo(url, title: string): void {
    this.titleService.setTitle(`${title} - Foglight`);

    this.router.navigateByUrl(url);
  }

  redirectToAUI(url, title): void {
    this.titleService.setTitle(`${title} - Foglight`);
    this.router.navigate([url]);
  }

  learnMoreClick(url: string): void {
    window.open(url, '_blank');
  }

  redirectToMonitor(
    viewId: string,
    instanceType: string,
    monitorDomain: string,
    aui: boolean = false
  ): void {
    if (this.isCloud) {
      if (aui) {
        this.subscriptionPendingService.checkExitingSubscription(
          viewId,
          instanceType,
          monitorDomain,
          true
        );
      } else {
        this.subscriptionPendingService.checkExitingSubscription(
          viewId,
          instanceType,
          monitorDomain,
          false
        );
      }
    } else {
      if (aui) {
        this.redirectToAUI(viewId, monitorDomain);
      } else {
        this.redirectTo(
          'wcf?name=general-view-aui-wrapper&viewId=' + viewId,
          monitorDomain
        );
      }
    }
  }

  onItemSelect(item, menu?): void {
    if (menu) {
      menu.toggle(item);
    }
    this.navbarService.getListofDownloadableFglams().subscribe((res) => {
      if (item.label === 'Foglight Agent Manager for Windows') {
        let fileId = this.getVersionFilename(res, 'FglAM', 'windows');
        window.open(
          `${window.location.origin}/console/installer/download?downloadId=${fileId}`,
          '_blank'
        );
      } else {
        let fileId = this.getVersionFilename(res, 'FglAM', 'linux');
        window.open(
          `${window.location.origin}/console/installer/download?downloadId=${fileId}`,
          '_blank'
        );
      }
    });
  }

  getVersionFilename(objects: any[], fileName: string, os: string): string {
    const object = objects.find(
      (obj) => obj.name.includes(fileName) && obj.os === os
    );

    if (object) {
      return object.id;
    } else {
      return '';
    }
  }

  onDropdownShow(): void {
    this.arrowState = !this.arrowState;
  }

  onDropdownHide(): void {
    this.arrowState = !this.arrowState;
    this.selectedItem = {};
    this.agentButton.nativeElement.blur();
  }
  openDocumentation(): void {
    this.utilService.openDocumentation();
  }
}
